import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Text } from "@mantine/core";
// form & submit hooks
import { useForm } from "@mantine/form";
import {
  useCompleteSetupMutation,
  useCompleteSetupCallbacks,
} from "src/services/accountActivation/completeSetupMutation";
// validation
import {
  calculateIfPasswordMeetsRequirements,
  extractErrorMessageFromRequirements,
} from "@pages/start/components/AccountActivateForm/formComponents/SetPassword/password-requirements/password-requirements";
// ui components
import { ContentHighlightCard } from "../../components/ContentHighlightCard/";
import { AccountActivateFormUI } from "../../components/AccountActivateForm";
import { CustomerInformation } from "../../components/CustomerInformation/CustomerInformation";
import { OrderSummary } from "../../components/OrderSummary/OrderSummary";
// style
import cx from "./account_activate_view.module.css";
// types
import type {
  AccountCreateLambdaResponse,
  AccountActivateFormData,
} from "src/services/accountActivation/accountActivationTypes";
import type { StartPageConfig } from "src/services/accountActivation/startPageConfig";
import type { MightierSupportedLanguage } from "@neuromotion-inc/mightier-ts-sdk";

interface AccountActivateViewProps {
  startPageConfig: StartPageConfig;
  accountCreateResp: AccountCreateLambdaResponse;
}

export const AccountActivateView: React.FC<AccountActivateViewProps> = (
  props,
) => {
  const { startPageConfig } = props;

  const { t, i18n } = useTranslation();

  const { onError, onSuccess } = useCompleteSetupCallbacks();

  const { mutateAsync, isPending } = useCompleteSetupMutation({
    onError,
    onSuccess,
  });

  const form = useForm<AccountActivateFormData>({
    initialValues: {
      password: "",
      confirmPassword: "",
      preferredLanguage:
        (i18n.resolvedLanguage as MightierSupportedLanguage) ?? "en",
      isMailOk: true,
      isEmailOk: false,
      isSmsOk: false,
      yesTermsAndPolicy: false,
      mcsIntakeConsentAccepted: false,
    },
    onValuesChange(values, previous) {
      if (values.preferredLanguage !== i18n.resolvedLanguage) {
        i18n.changeLanguage(values.preferredLanguage);
      }
    },
    validate: {
      password: (value) => {
        const requirements = calculateIfPasswordMeetsRequirements(value, t);
        return extractErrorMessageFromRequirements(requirements);
      },
      confirmPassword: (value, values) => {
        if (!value) {
          return t("startPage.validation.confirmPasswordRequired");
        }
        if (value && value !== values.password) {
          return t("startPage.validation.confirmPasswordMismatch");
        }
      },
      yesTermsAndPolicy: (value) => {
        // NOTE: if startPageConfig doesnt require terms/condition opt in
        if (startPageConfig?.showTermsConditions && !value) {
          return t("startPage.validation.termsAndPolicyRequired");
        }
      },
      mcsIntakeConsentAccepted: (value) => {
        if (startPageConfig?.showMcsConsentToTreat && !value) {
          return t("startPage.validation.mcsConsentRequired");
        }
      },
    },
  });

  const handleSubmit = async (
    values: AccountActivateFormData,
  ): Promise<void> => {
    await mutateAsync(values);
  };

  const handleValidationErrors = (errors: Record<string, unknown>): void => {
    const firstErrorPath = Object.keys(errors)[0];
    if (!firstErrorPath) {
      return;
    }
    const inputNode = form.getInputNode(firstErrorPath);
    inputNode?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    inputNode?.focus();
  };

  // note that the FIRST TIME the user lands here is the only time that
  // the backend lambda authenticates them (because their password isn't set yet)
  const hasNotSetPasswordSoCanUpdateAccount = !!props.accountCreateResp.auth;

  return (
    <>
      <ContentHighlightCard>
        {/* blue "we registered ur account" note */}
        <Box className={cx.note}>
          {t("startPage.weRegisteredYourAccount")}{" "}
          <Text className={cx["email-wrapped"]}>
            {props.accountCreateResp.order.user.email}
          </Text>
        </Box>

        {hasNotSetPasswordSoCanUpdateAccount ? (
          <AccountActivateFormUI
            form={form}
            handleSubmit={handleSubmit}
            handleValidationErrors={handleValidationErrors}
            isMutationLoading={isPending}
            startPageConfig={startPageConfig}
          />
        ) : null}
      </ContentHighlightCard>
      {/* only show order summary if dictated by pageConfig! */}
      {props.startPageConfig.showOrderSummary ? (
        <OrderSummary accountCreateResp={props.accountCreateResp} />
      ) : null}
      {/* customer info always shown - however pageConfig dictates what data is shown */}
      <CustomerInformation
        accountCreateResp={props.accountCreateResp}
        showBillingAddress={
          props.startPageConfig?.customerInfo.showBillingAddress ?? false
        }
      />
    </>
  );
};
